import { useMemo } from 'react';
import { Chart } from '@tanstack/react-charts';
import { defineChart, link, rect, text } from '@tanstack/charts';
import { sankeyDiagram } from '@tanstack/charts/network/sankey';
import { STATUS_TONE_COLORS } from '@/components/ui/statusTone.ts';
import { cn } from '@/lib/utils.ts';

export interface FunnelStep {
  id: string;
  label: string;
  count: number;
  /** Ilu odpadło na tym progu; już nieujemne — liczy je adapter. */
  drop: number;
  color: string;
}

interface FunnelSankeyChartProps extends React.HTMLAttributes<HTMLDivElement> {
  steps: FunnelStep[];
  height?: number;
  ariaLabel: string;
}

/** Węzeł odpływu dla progu `i` — osobny, bo strata ma własną kolumnę i barwę. */
const dropNodeId = (stepId: string) => `${stepId}--drop`;

/**
 * Lejek zapisu jako diagram przepływu: ilu weszło, ilu przeszło dalej, ilu
 * odpadło. Jedyne miejsce, które zna `@tanstack/charts/network/sankey`.
 *
 * Odpływ jest własnym węzłem, a nie samym zwężeniem wstążki — ma własną barwę
 * i własną kolumnę, więc strata jest widoczna, a nie domyślana z różnicy.
 */
const FunnelSankeyChart = ({
  steps,
  height = 300,
  ariaLabel,
  className,
  ...props
}: FunnelSankeyChartProps) => {
  const definition = useMemo(() => {
    const nodes = steps.flatMap((step) => {
      const lost = step.drop;

      return [
        {
          id: step.id,
          label: step.label,
          count: step.count,
          color: step.color,
          isDrop: false,
        },
        ...(lost > 0
          ? [
              {
                id: dropNodeId(step.id),
                label: `−${lost}`,
                count: lost,
                color: STATUS_TONE_COLORS.error.dot,
                isDrop: true,
              },
            ]
          : []),
      ];
    });

    const links = steps.flatMap((step, index) => {
      const next = steps[index + 1];
      if (!next) return [];
      const lost = step.drop;

      return [
        { source: step.id, target: next.id, value: next.count },
        ...(lost > 0
          ? [{ source: step.id, target: dropNodeId(step.id), value: lost }]
          : []),
      ];
    });

    return defineChart({
      marks: [
        sankeyDiagram({
          nodes,
          links,
          nodeKey: 'id',
          source: 'source',
          target: 'target',
          value: 'value',
          align: 'left',
          nodePadding: 34,
          // Podpisy progów wystają poza słupki, więc marginesy muszą je
          // pomieścić — inaczej pierwszy i ostatni ucinają się o krawędź.
          inset: { left: 76, right: 76, top: 30, bottom: 30 },
          marks: ({ nodes: layoutNodes, links: layoutLinks }) =>
            [
              link(layoutLinks, {
                x1: 'x1',
                y1: 'y1',
                x2: 'x2',
                y2: 'y2',
                key: 'key',
                strokeWidth: (flow) => flow.width,
                stroke: STATUS_TONE_COLORS.neutral.dot,
                strokeOpacity: 0.4,
                // Bez tego gruba wstążka dostaje okrągłe zakończenia i przy
                // słupku rozlewa się w kleks.
                lineCap: 'butt',
              }),
              rect(layoutNodes, {
                x1: 'x0',
                x2: 'x1',
                y1: 'y0',
                y2: 'y1',
                key: 'key',
                inset: 0,
                color: (node) => node.data.id,
              }),
              text(layoutNodes, {
                x: 'x',
                y: (node) => node.y0 - 8,
                text: (node) => node.data.label,
                key: 'key',
                fill: 'currentColor',
                fontSize: 11.5,
                fontWeight: 600,
              }),
              text(layoutNodes, {
                x: 'x',
                y: (node) => node.y1 + 15,
                // Odpływ ma liczbę już w podpisie górnym („−12”); drugi raz
                // wchodziłby pod sąsiedni próg i nachodził na jego etykietę.
                text: (node) =>
                  node.data.isDrop ? '' : String(node.data.count),
                key: 'key',
                fill: 'currentColor',
                fontSize: 14,
                fontWeight: 800,
              }),
            ] as const,
        }),
      ],
      color: {
        domain: nodes.map((node) => node.id),
        range: nodes.map((node) => node.color),
      },
      guides: false,
      margin: 0,
    });
  }, [steps]);

  return (
    <div className={cn('text-neutral-700', className)} {...props}>
      <Chart definition={definition} height={height} ariaLabel={ariaLabel} />
    </div>
  );
};

export default FunnelSankeyChart;
